/**
 * GsiMegaMenu — Web Component (Dawn adaptation of the GSI Mega Menu spec)
 * Manages Shop/Explore panels, mobile nav drawer, scroll hide/reveal,
 * keyboard nav, and opening Dawn's cart drawer from the cart button.
 */
class GsiMegaMenu extends HTMLElement {
  connectedCallback() {
    this._bar      = this.querySelector('.gsi-nav__bar');
    this._section  = this.closest('.shopify-section');   // sticky/hide target (see gsi-nav-mega.liquid)
    this._triggers = this.querySelectorAll('[data-mega-trigger]');
    this._panels   = this.querySelectorAll('[data-mega-panel]');
    this._overlay  = this.querySelector('[data-nav-overlay]');
    this._mobileBtn   = this.querySelector('[data-mobile-nav-trigger]');
    this._mobileClose = this.querySelector('[data-mobile-nav-close]');
    this._mobileNav   = this.querySelector('#gsi-mobile-nav');
    this._cartBtn     = this.querySelector('[data-cart-trigger]');

    this._lastScroll = window.scrollY;
    this._ticking    = false;

    this._bindTriggers();
    this._bindMobileNav();
    this._bindCart();
    this._bindCartCount();
    this._bindScroll();
    this._bindFocusReveal();
    this._bindOutsideClick();
    this._bindEscape();
  }

  // ── a11y: reveal header on keyboard focus ───────────────────────────
  // If focus enters the header while it's hidden (e.g. Shift+Tab back into
  // the nav, or a screen-reader jump), slide it back into view so the focused
  // control is never left off-screen and invisible (WCAG 2.4.7 Focus Visible).
  _bindFocusReveal() {
    this.addEventListener('focusin', () => {
      (this._section || this._bar).classList.remove('gsi-nav-hidden');
    });
  }

  // ── Live cart count ─────────────────────────────────────────────────
  // Dawn live-updates its own #cart-icon-bubble (removed with the core header),
  // so subscribe to Dawn's cartUpdate pub/sub and refresh our count from /cart.js.
  _bindCartCount() {
    this._cartCountEl = this.querySelector('[data-cart-count]');
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) {
      this._cartUnsub = subscribe(PUB_SUB_EVENTS.cartUpdate, () => this._refreshCartCount());
    }
  }

  _refreshCartCount() {
    if (!this._cartCountEl) return;
    const root = (window.Shopify && Shopify.routes && Shopify.routes.root) ? Shopify.routes.root : '/';
    fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } })
      .then(r => r.json())
      .then(cart => {
        const n = cart.item_count;
        this._cartCountEl.textContent = n;
        if (n > 0) this._cartCountEl.removeAttribute('hidden');
        else this._cartCountEl.setAttribute('hidden', '');
      })
      .catch(() => {});
  }

  _bindTriggers() {
    this._triggers.forEach(trigger => {
      trigger.addEventListener('click',   e => this._onTriggerClick(e));
      trigger.addEventListener('keydown', e => this._onTriggerKeydown(e));
    });
  }

  _onTriggerClick(e) {
    const id    = e.currentTarget.dataset.megaTrigger;
    const panel = this.querySelector(`[data-mega-panel="${id}"]`);
    const isOpen = panel.getAttribute('aria-hidden') === 'false';
    this._closeAll();
    if (!isOpen) this._open(e.currentTarget, panel);
  }

  _onTriggerKeydown(e) {
    // Enter/Space: let the native <button> click fire once (handling it here too = double toggle). a11y 2.1.1 (M11)
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      const id    = e.currentTarget.dataset.megaTrigger;
      const panel = this.querySelector(`[data-mega-panel="${id}"]`);
      if (panel.getAttribute('aria-hidden') === 'false') {
        panel.querySelector('[data-focus-first]')?.focus();
      }
    }
  }

  _open(trigger, panel) {
    trigger.setAttribute('aria-expanded', 'true');
    panel.setAttribute('aria-hidden', 'false');
    this._overlay.classList.add('is-active');
    panel.querySelector('[data-focus-first]')?.focus();
    this._trackEvent('gsi_mega_menu_open', { menu: trigger.dataset.megaTrigger });
  }

  _closeAll() {
    this._triggers.forEach(t => t.setAttribute('aria-expanded', 'false'));
    this._panels.forEach(p => p.setAttribute('aria-hidden', 'true'));
    this._overlay.classList.remove('is-active');
  }

  // ── Cart: open Dawn's independent cart drawer ───────────────────────
  _bindCart() {
    this._cartBtn?.addEventListener('click', () => {
      const drawer = document.querySelector('cart-drawer');
      if (drawer && typeof drawer.open === 'function') {
        drawer.open(this._cartBtn);
      } else {
        window.location.href = '/cart';
      }
    });
  }

  // ── Mobile nav ──────────────────────────────────────────────────────
  _bindMobileNav() {
    this._mobileBtn?.addEventListener('click', () => this._openMobileNav());
    this._mobileClose?.addEventListener('click', () => this._closeMobileNav());
  }

  _openMobileNav() {
    this._mobileNav.removeAttribute('inert');      // a11y 4.1.2/2.4.3 (B2): re-enable focus + AT
    this._mobileNav.setAttribute('aria-hidden', 'false');
    this._mobileBtn.setAttribute('aria-expanded', 'true');
    this._overlay.classList.add('is-active');
    document.body.style.overflow = 'hidden';
    this._trapFocus(this._mobileNav);
    this._mobileClose?.focus();
  }

  _closeMobileNav() {
    if (this._mobileNav?.getAttribute('aria-hidden') !== 'false') return; // not open — don't steal focus
    this._mobileNav.setAttribute('aria-hidden', 'true');
    this._mobileBtn.setAttribute('aria-expanded', 'false');
    this._overlay.classList.remove('is-active');
    document.body.style.overflow = '';
    this._releaseTrap(this._mobileNav);            // a11y 2.1.2: remove the trap handler so it can't leak
    this._mobileBtn?.focus();
    this._mobileNav.setAttribute('inert', '');     // links leave the tab order + AT tree while closed (B2)
  }

  _releaseTrap(container) {
    if (container && container._focusTrapHandler) {
      container.removeEventListener('keydown', container._focusTrapHandler);
      container._focusTrapHandler = null;
    }
  }

  _trapFocus(container) {
    this._releaseTrap(container); // guard against a stale handler from a prior open
    const focusable = Array.from(container.querySelectorAll(
      'a[href], button:not([disabled]), input, select, textarea, [tabindex]:not([tabindex="-1"])'
    ));
    if (!focusable.length) return;
    const first = focusable[0];
    const last  = focusable[focusable.length - 1];
    container._focusTrapHandler = e => {
      if (e.key !== 'Tab') return;
      if (e.shiftKey) {
        if (document.activeElement === first) { e.preventDefault(); last.focus(); }
      } else {
        if (document.activeElement === last) { e.preventDefault(); first.focus(); }
      }
    };
    container.addEventListener('keydown', container._focusTrapHandler);
  }

  // ── Scroll behavior ─────────────────────────────────────────────────
  _bindScroll() {
    window.addEventListener('scroll', () => {
      if (!this._ticking) {
        requestAnimationFrame(() => { this._handleScroll(); this._ticking = false; });
        this._ticking = true;
      }
    }, { passive: true });
  }

  _handleScroll() {
    const el = this._section || this._bar;   // sticky header slides as one unit
    const current = window.scrollY;
    if (current <= 0) {
      el.classList.remove('gsi-nav-hidden');
    } else if (current > this._lastScroll && current > 80 && !this.contains(document.activeElement)) {
      // Hide on scroll down — but never while keyboard focus is inside the
      // header, so we don't pull a focused control off-screen (WCAG 2.4.7).
      el.classList.add('gsi-nav-hidden');
      this._closeAll();
    } else {
      el.classList.remove('gsi-nav-hidden');
    }
    this._lastScroll = current;
  }

  // ── Outside click & Escape ──────────────────────────────────────────
  _bindOutsideClick() {
    this._overlay.addEventListener('click', () => {
      this._closeAll();
      this._closeMobileNav();
    });
  }

  _bindEscape() {
    document.addEventListener('keydown', e => {
      if (e.key !== 'Escape') return;
      const wasOpen = Array.from(this._panels).some(p => p.getAttribute('aria-hidden') === 'false');
      const mobileOpen = this._mobileNav?.getAttribute('aria-hidden') === 'false';
      this._closeAll();
      if (mobileOpen) {
        this._closeMobileNav();
      } else if (wasOpen) {
        const activeTrigger = this.querySelector('[aria-expanded="true"]') ||
                              this.querySelector('[data-mega-trigger]');
        activeTrigger?.focus();
      }
    });
  }

  _trackEvent(name, params = {}) {
    if (typeof gtag !== 'undefined') { gtag('event', name, params); }
  }
}

customElements.define('gsi-mega-menu', GsiMegaMenu);

// ── Editorial tile + activity click tracking (guarded) ───────────────
document.querySelectorAll('.gsi-editorial-tile__cta').forEach(cta => {
  cta.addEventListener('click', () => {
    if (typeof gtag !== 'undefined') {
      gtag('event', 'gsi_nav_editorial_click', {
        campaign: cta.closest('[data-mega-panel]')?.dataset?.megaPanel,
        cta_text: cta.textContent.trim(),
      });
    }
  });
});
document.querySelectorAll('.gsi-mega__activity-link').forEach(link => {
  link.addEventListener('click', () => {
    if (typeof gtag !== 'undefined') {
      gtag('event', 'gsi_nav_activity_click', { activity_name: link.textContent.trim() });
    }
  });
});
